import React, {
  useState, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  DatePicker,
  Typography,
} from 'antd';
import moment from 'moment';

import CompanySettingsCard from '../CompanySettingsCard';
import CompanyEditRow from '../CompanyEditRow';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';
import OnTraccrNumberInput, { snapToStep } from '../../common/inputs/OnTraccrNumberInput';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import ConfirmPasswordModal from '../ConfirmPasswordModal';
import {
  saveCompanyPaidFeatures,
  saveCompanyConfiguration,
} from '../state/settings.actions';
import { ALL_PAID_FLAGS, PAID_SETTINGS } from '../../constants/Flags';
import { GB } from '../../files/fileHelpers';
import { isNullOrUndefined } from '../../helpers/helpers';

const { Text } = Typography;

const STORAGE_LIMIT_CONFIG = {
  min: 200,
  max: 5000,
  step: 100,
};

export default function CompanyConfiguration() {
  const dispatch = useDispatch();
  const company = useSelector((state) => state.settings.company);

  const {
    subscriptionEnd,
    userLimit,
    storageLimit,
    paidFlags = [],
    isExternalFormEnabled,
    paidPDF,
  } = company;

  const [draftSubscriptionEnd, setDraftSubscriptionEnd] = useState(moment(subscriptionEnd.toJSDate()));
  const [draftUserLimit, setDraftUserLimit] = useState(userLimit);
  const [draftStorageLimit, setDraftStorageLimit] = useState(
    !isNullOrUndefined(storageLimit) ? (storageLimit / GB) : STORAGE_LIMIT_CONFIG.min,
  );
  const [passwordConfirmFor, setPasswordConfirmFor] = useState(null);
  const [draftPaidBooleans, setDraftPaidBooleans] = useState({
    isExternalFormEnabled: !!isExternalFormEnabled,
    paidPDF: !!paidPDF,
  });
  const [draftPaidFlags, setDraftPaidFlags] = useState(paidFlags);

  const handleUserLimitDraftChange = (value) => {
    setDraftUserLimit(value);
  };

  const handleStorageLimitDraftChange = (value) => {
    setDraftStorageLimit(value);
  };

  const handleStorageLimitBlur = () => {
    setDraftStorageLimit((prev) => snapToStep(
      prev,
      STORAGE_LIMIT_CONFIG.step,
      STORAGE_LIMIT_CONFIG.min,
      STORAGE_LIMIT_CONFIG.max,
    ));
  };

  const handleBooleanToggle = (field) => (checked) => {
    setDraftPaidBooleans((prev) => ({ ...prev, [field]: checked }));
  };

  const handleFlagToggle = (flag) => (checked) => {
    setDraftPaidFlags((prev) => {
      const next = new Set(prev);
      if (checked) next.add(flag);
      else next.delete(flag);
      return [...next];
    });
  };

  const openConfigurationConfirmModal = () => {
    setPasswordConfirmFor('configuration');
  };

  const openPaidFeaturesConfirmModal = () => {
    setPasswordConfirmFor('paidFeatures');
  };

  const closePasswordConfirmModal = () => {
    setPasswordConfirmFor(null);
  };

  const handlePasswordModalConfirm = useCallback(async (password) => {
    if (passwordConfirmFor === 'configuration') {
      const payload = {
        subscriptionEnd: draftSubscriptionEnd.toISOString(),
        userLimit: draftUserLimit,
        storageLimit: draftStorageLimit,
        password,
      };
      return dispatch(saveCompanyConfiguration(payload));
    }
    if (passwordConfirmFor === 'paidFeatures') {
      const payload = {
        password,
        paidFlags: draftPaidFlags,
        ...draftPaidBooleans,
      };
      return dispatch(saveCompanyPaidFeatures(payload));
    }
    return false;
  }, [
    passwordConfirmFor,
    draftSubscriptionEnd,
    draftUserLimit,
    draftStorageLimit,
    draftPaidFlags,
    draftPaidBooleans,
  ]);

  return (
    <Row
      justify="start"
      style={{
        height: '100vh',
        overflowY: 'auto',
        paddingBottom: 100,
      }}
    >
      <Col span={24}>
        <ConfirmPasswordModal
          visible={passwordConfirmFor != null}
          onClose={closePasswordConfirmModal}
          onConfirm={handlePasswordModalConfirm}
        />

        <CompanySettingsCard
          title="Subscription & limits"
          leftHeaderView={
            <OnTraccrButton
              title="Save"
              onClick={openConfigurationConfirmModal}
            />
          }
        >
          <Row
            align="middle"
            justify="space-between"
            style={{ marginBottom: 16, marginTop: 16 }}
          >
            <Col>
              <Text>Subscription end date</Text>
            </Col>
            <Col>
              <DatePicker
                value={draftSubscriptionEnd}
                onChange={(m) => setDraftSubscriptionEnd(m)}
                style={{ width: 150 }}
              />
            </Col>
          </Row>
          <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
            <Col>
              <Text>User limit</Text>
            </Col>
            <Col>
              <OnTraccrNumberInput
                min={1}
                value={draftUserLimit}
                onChange={handleUserLimitDraftChange}
                style={{ width: 150 }}
              />
            </Col>
          </Row>
          <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
            <Col>
              <Text>Storage limit (GB)</Text>
            </Col>
            <Col>
              <OnTraccrNumberInput
                min={STORAGE_LIMIT_CONFIG.min}
                max={STORAGE_LIMIT_CONFIG.max}
                step={STORAGE_LIMIT_CONFIG.step}
                value={draftStorageLimit}
                onChange={handleStorageLimitDraftChange}
                onBlur={handleStorageLimitBlur}
                style={{ width: 150 }}
              />
            </Col>
          </Row>
        </CompanySettingsCard>

        <CompanySettingsCard
          title="Paid features"
          leftHeaderView={
            <OnTraccrButton
              title="Save"
              onClick={openPaidFeaturesConfirmModal}
            />
          }
        >
          {PAID_SETTINGS.map(({ field, label, description }) => (
            <CompanyEditRow
              key={field}
              title={label}
              helpText={description}
            >
              <OnTraccrSwitch
                value={!!draftPaidBooleans[field]}
                onChange={handleBooleanToggle(field)}
              />
            </CompanyEditRow>
          ))}
          {ALL_PAID_FLAGS.map(({ flag, label, description }, index) => (
            <CompanyEditRow
              key={flag}
              title={label}
              helpText={description}
              divider={index !== ALL_PAID_FLAGS.length - 1}
            >
              <OnTraccrSwitch
                value={draftPaidFlags.includes(flag)}
                onChange={handleFlagToggle(flag)}
              />
            </CompanyEditRow>
          ))}
        </CompanySettingsCard>
      </Col>
    </Row>
  );
}
